import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, RotateCcw, Layers, Type, Image as ImageIcon, Phone, Palette, Home, Plus, Trash2, FileEdit } from "lucide-react";
import FileUpload from "@/components/common/FileUpload";
import { useSettings } from "@/context/SettingsContext";

const TABS = [
  { id: "foundation", label: "Yayasan", icon: Layers },
  { id: "navbar", label: "Header", icon: Type },
  { id: "hero", label: "Hero", icon: ImageIcon },
  { id: "footer", label: "Footer", icon: Palette },
  { id: "contact", label: "Kontak", icon: Phone },
  { id: "homepage", label: "Beranda", icon: Home },
  { id: "about_page", label: "Hlm. Tentang", icon: FileEdit },
  { id: "impact_page", label: "Hlm. Impact", icon: FileEdit },
  { id: "projects_page", label: "Hlm. Proyek", icon: FileEdit },
  { id: "programs_page", label: "Hlm. Program", icon: FileEdit },
  { id: "news_page", label: "Hlm. Berita", icon: FileEdit },
  { id: "gallery_page", label: "Hlm. Galeri", icon: FileEdit },
  { id: "partners_page", label: "Hlm. Mitra", icon: FileEdit },
  { id: "publications_page", label: "Hlm. Publikasi", icon: FileEdit },
  { id: "team_page", label: "Hlm. Tim", icon: FileEdit },
  { id: "contact_page", label: "Hlm. Kontak", icon: FileEdit },
  { id: "volunteer_page", label: "Hlm. Relawan", icon: FileEdit },
  { id: "bank_accounts", label: "Rekening", icon: Layers },
];

export default function AdminSiteSettings() {
  const { reload: reloadContext } = useSettings();
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState("foundation");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/site/settings");
      setSettings(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/site/settings", patch);
      setSettings(data);
      await reloadContext();
      toast.success("Pengaturan tersimpan");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const resetSection = async () => {
    if (!confirm(`Reset section '${tab}' ke default?`)) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/site/settings/reset?section=${tab}`);
      setSettings(data);
      await reloadContext();
      toast.success("Section direset ke default");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) return <div className="text-slate-500">Memuat...</div>;

  return (
    <div data-testid="admin-site-settings">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Pengaturan Situs</h1>
          <p className="text-slate-600 mt-1">Edit teks, gambar, kontak, dan tampilan situs. Perubahan langsung tampil di semua halaman.</p>
        </div>
        <button onClick={resetSection} disabled={saving} className="btn-ghost text-sm" data-testid="reset-section">
          <RotateCcw className="h-3.5 w-3.5" /> Reset Section Ini
        </button>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-slate-200 mb-8">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`settings-tab-${t.id}`}
            className={`px-5 py-3 text-sm font-semibold flex items-center gap-2 border-b-2 transition ${tab === t.id ? "border-[var(--brand-blue)] text-[var(--brand-blue)]" : "border-transparent text-slate-500 hover:text-slate-800"}`}
          >
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "foundation" && <SectionEditor section="foundation" data={settings.foundation} onSave={save} saving={saving} fields={[
        { name: "legal_name", label: "Nama Legal Yayasan" },
        { name: "short_name", label: "Nama Singkat" },
        { name: "tagline_sundanese", label: "Tagline Sunda" },
        { name: "tagline_english", label: "Tagline English" },
        { name: "tagline_translation", label: "Terjemahan Tagline" },
        { name: "logo_url", label: "URL Logo", type: "image" },
      ]} />}

      {tab === "navbar" && <SectionEditor section="navbar" data={settings.navbar} onSave={save} saving={saving} fields={[
        { name: "brand_text", label: "Brand Text (SAUYUNAN)" },
        { name: "brand_subtitle", label: "Brand Subtitle (Foundation)" },
        { name: "donate_button_label", label: "Label Tombol Donasi" },
        { name: "donate_button_url", label: "URL Tombol Donasi" },
      ]} />}

      {tab === "hero" && <SectionEditor section="hero" data={settings.hero} onSave={save} saving={saving} fields={[
        { name: "badge_text", label: "Badge (di atas judul)" },
        { name: "title_line1", label: "Judul Baris 1", help: "Contoh: Menghubungkan" },
        { name: "title_italic", label: "Kata Italic (biru)", help: "Contoh: harapan" },
        { name: "title_line2", label: "Judul Baris 2", help: "Contoh: menjadi" },
        { name: "title_accent", label: "Kata Aksen (orange)", help: "Contoh: dampak nyata" },
        { name: "description", label: "Deskripsi Hero", type: "textarea", rows: 4 },
        { name: "cta_donate_label", label: "Label CTA Donasi" },
        { name: "cta_donate_url", label: "URL CTA Donasi" },
        { name: "cta_volunteer_label", label: "Label CTA Volunteer" },
        { name: "cta_volunteer_url", label: "URL CTA Volunteer" },
        { name: "cta_learn_label", label: "Label CTA Kenali" },
        { name: "cta_learn_url", label: "URL CTA Kenali" },
        { name: "image_url", label: "Gambar Hero", type: "image" },
        { name: "stat_label", label: "Label Statistik" },
        { name: "stat_value", label: "Nilai Statistik" },
        { name: "stat_desc", label: "Deskripsi Statistik" },
        { name: "badge_live_text", label: "Badge Live" },
      ]} />}

      {tab === "footer" && <SectionEditor section="footer" data={settings.footer} onSave={save} saving={saving} fields={[
        { name: "description", label: "Deskripsi Footer", type: "textarea", rows: 3 },
        { name: "newsletter_eyebrow", label: "Newsletter Eyebrow" },
        { name: "newsletter_title", label: "Newsletter Title", type: "textarea", rows: 2 },
        { name: "newsletter_note", label: "Newsletter Note" },
        { name: "col_explore_title", label: "Judul Kolom 'Jelajahi'" },
        { name: "col_action_title", label: "Judul Kolom 'Aksi'" },
        { name: "col_contact_title", label: "Judul Kolom 'Hubungi Kami'" },
        { name: "privacy_label", label: "Label Privacy" },
        { name: "privacy_url", label: "URL Privacy" },
        { name: "terms_label", label: "Label Terms" },
        { name: "terms_url", label: "URL Terms" },
        { name: "copyright_extra", label: "Copyright Extra Text (opsional)" },
        { name: "show_admin_link", label: "Tampilkan link Admin di footer", type: "boolean" },
      ]} />}

      {tab === "contact" && (
        <>
          <SectionEditor section="contact" data={settings.contact} onSave={save} saving={saving} fields={[
            { name: "email", label: "Email" },
            { name: "address_line1", label: "Alamat Baris 1" },
            { name: "address_line2", label: "Alamat Baris 2" },
            { name: "address_line3", label: "Alamat Baris 3" },
            { name: "office_hours", label: "Jam Operasional" },
            { name: "map_embed_src", label: "URL Google Maps Embed" },
          ]} extraChildren={
            <>
              <ListArrayEditor title="Nomor Telepon" section="contact" field="phones" items={settings.contact?.phones || []} onSave={save} placeholder="+62 812-345-6789" saving={saving} />
              <SocialsEditor socials={settings.contact?.socials || {}} onSave={save} saving={saving} />
            </>
          } />
        </>
      )}

      {tab === "homepage" && <SectionEditor section="homepage" data={settings.homepage} onSave={save} saving={saving} fields={[
        { name: "show_vision_mission", label: "Tampilkan Section Vision & Mission", type: "boolean" },
        { name: "show_programs", label: "Tampilkan Section Program", type: "boolean" },
        { name: "show_philosophy", label: "Tampilkan Section Filosofi", type: "boolean" },
        { name: "show_impact", label: "Tampilkan Section Impact", type: "boolean" },
        { name: "show_sdg", label: "Tampilkan Section SDG", type: "boolean" },
        { name: "show_news", label: "Tampilkan Section Berita Terbaru", type: "boolean" },
        { name: "show_gallery", label: "Tampilkan Section Galeri", type: "boolean" },
        { name: "show_testimonials", label: "Tampilkan Section Testimoni", type: "boolean" },
        { name: "show_partners", label: "Tampilkan Section Mitra", type: "boolean" },
        { name: "show_cta", label: "Tampilkan Section CTA", type: "boolean" },
        { name: "programs_count", label: "Jumlah Program yang Ditampilkan", type: "number" },
        { name: "news_count", label: "Jumlah Berita yang Ditampilkan", type: "number" },
      ]} />}

      {tab === "about_page" && <SectionEditor section="about_page" data={settings.about_page} onSave={save} saving={saving} fields={[
        { name: "title", label: "Judul Halaman" },
        { name: "executiveSummary", label: "Ringkasan Eksekutif", type: "textarea", rows: 4 },
        { name: "catalystStatement", label: "Katalisator (Quote Besar)", type: "textarea", rows: 3 },
        { name: "vision", label: "Visi Yayasan", type: "textarea", rows: 3 },
        { name: "mission", label: "Misi Yayasan (Format JSON Array)", type: "json" },
        { name: "history", label: "Sejarah Yayasan (Format JSON Array)", type: "json" },
        { name: "philosophy", label: "Filosofi Sunda (Format JSON Array)", type: "json" },
        { name: "values", label: "Nilai-nilai Organisasi (Format JSON Array)", type: "json" },
      ]} extraChildren={
        <p className="text-xs text-slate-500 mt-2">Catatan: Pastikan mengubah data Misi, Sejarah, Filosofi, dan Nilai-nilai menggunakan format JSON yang valid agar tampilan website tetap terjaga.</p>
      }/>}

      {tab === "impact_page" && <SectionEditor section="impact_page" data={settings.impact_page} onSave={save} saving={saving} fields={[
        { name: "title", label: "Judul Halaman Impact" },
        { name: "description", label: "Deskripsi", type: "textarea", rows: 4 },
      ]} />}

      {tab === "projects_page" && <SectionEditor section="projects_page" data={settings.projects_page} onSave={save} saving={saving} fields={[
        { name: "title", label: "Judul Halaman Proyek" },
        { name: "description", label: "Deskripsi", type: "textarea", rows: 4 },
      ]} />}

      {tab === "programs_page" && <SectionEditor section="programs_page" data={settings.programs_page} onSave={save} saving={saving} fields={[
        { name: "eyebrow", label: "Eyebrow (Teks kecil di atas)" },
        { name: "title", label: "Judul Halaman Program" },
        { name: "description", label: "Deskripsi", type: "textarea", rows: 4 },
        { name: "image", label: "Gambar Header", type: "image" },
      ]} />}

      {tab === "news_page" && <SectionEditor section="news_page" data={settings.news_page} onSave={save} saving={saving} fields={[
        { name: "eyebrow", label: "Eyebrow (Teks kecil di atas)" },
        { name: "title", label: "Judul Halaman Berita" },
        { name: "description", label: "Deskripsi", type: "textarea", rows: 4 },
        { name: "image", label: "Gambar Header", type: "image" },
      ]} />}

      {tab === "gallery_page" && <SectionEditor section="gallery_page" data={settings.gallery_page} onSave={save} saving={saving} fields={[
        { name: "eyebrow", label: "Eyebrow (Teks kecil di atas)" },
        { name: "title", label: "Judul Halaman Galeri" },
        { name: "description", label: "Deskripsi", type: "textarea", rows: 4 },
        { name: "image", label: "Gambar Header", type: "image" },
      ]} />}

      {tab === "partners_page" && <SectionEditor section="partners_page" data={settings.partners_page} onSave={save} saving={saving} fields={[
        { name: "eyebrow", label: "Eyebrow (Teks kecil di atas)" },
        { name: "title", label: "Judul Halaman Mitra" },
        { name: "description", label: "Deskripsi", type: "textarea", rows: 4 },
        { name: "image", label: "Gambar Header", type: "image" },
      ]} />}

      {tab === "publications_page" && <SectionEditor section="publications_page" data={settings.publications_page} onSave={save} saving={saving} fields={[
        { name: "eyebrow", label: "Eyebrow (Teks kecil di atas)" },
        { name: "title", label: "Judul Halaman Publikasi" },
        { name: "description", label: "Deskripsi", type: "textarea", rows: 4 },
        { name: "image", label: "Gambar Header", type: "image" },
      ]} />}

      {tab === "team_page" && <SectionEditor section="team_page" data={settings.team_page} onSave={save} saving={saving} fields={[
        { name: "eyebrow", label: "Eyebrow (Teks kecil di atas)" },
        { name: "title", label: "Judul Halaman Tim" },
        { name: "description", label: "Deskripsi", type: "textarea", rows: 4 },
        { name: "image", label: "Gambar Header", type: "image" },
      ]} />}

      {tab === "contact_page" && <SectionEditor section="contact_page" data={settings.contact_page} onSave={save} saving={saving} fields={[
        { name: "eyebrow", label: "Eyebrow (Teks kecil di atas)" },
        { name: "title", label: "Judul Halaman Kontak" },
        { name: "description", label: "Deskripsi", type: "textarea", rows: 4 },
        { name: "image", label: "Gambar Header (Opsional)", type: "image" },
      ]} />}

      {tab === "volunteer_page" && <SectionEditor section="volunteer_page" data={settings.volunteer_page} onSave={save} saving={saving} fields={[
        { name: "eyebrow", label: "Eyebrow (Teks kecil di atas)" },
        { name: "title", label: "Judul Halaman Relawan" },
        { name: "description", label: "Deskripsi", type: "textarea", rows: 4 },
        { name: "image", label: "Gambar Header (Opsional)", type: "image" },
        { name: "faq", label: "FAQ (Format JSON Array)", type: "json" }
      ]} extraChildren={
        <p className="text-xs text-slate-500 mt-2">Catatan: Pastikan mengubah data FAQ menggunakan format JSON yang valid. Contoh: <code>[{"{"}"q": "Pertanyaan", "a": "Jawaban"{"}"}]</code></p>
      } />}

      {tab === "bank_accounts" && (
        <BankAccountsEditor accounts={settings.bank_accounts || []} onSave={save} saving={saving} />
      )}
    </div>
  );
}

// ============= Sub components =============

function SectionEditor({ section, data, onSave, saving, fields, extraChildren }) {
  const [form, setForm] = useState(data || {});
  useEffect(() => { setForm(data || {}); }, [data]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ [section]: form });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5 max-w-3xl" data-testid={`settings-form-${section}`}>
      {fields.map((f) => (
        <FieldRow key={f.name} field={f} value={form[f.name]} onChange={(v) => setForm({ ...form, [f.name]: v })} />
      ))}
      {extraChildren}
      <button type="submit" disabled={saving} data-testid={`save-${section}`} className="btn-secondary">
        <Save className="h-4 w-4" /> {saving ? "Menyimpan..." : "Simpan Perubahan"}
      </button>
    </form>
  );
}

function FieldRow({ field, value, onChange }) {
  const base = "w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[var(--brand-blue)]";
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-700 mb-1.5">{field.label}</label>
      {field.type === "boolean" ? (
        <label className="inline-flex items-center gap-2">
          <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4" />
          <span className="text-sm text-slate-700">{value ? "Aktif" : "Nonaktif"}</span>
        </label>
      ) : field.type === "textarea" ? (
        <textarea rows={field.rows || 3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={base} />
      ) : field.type === "json" ? (
        <textarea rows={field.rows || 10} value={typeof value === 'string' ? value : JSON.stringify(value, null, 2) || ""} onChange={(e) => {
          try {
            onChange(JSON.parse(e.target.value));
          } catch(err) {
            onChange(e.target.value); // keep as string if invalid, to allow typing
          }
        }} className={base + " font-mono text-xs"} placeholder="Masukkan dalam format JSON yang valid" />
      ) : field.type === "image" ? (
        <FileUpload value={value || ""} onChange={onChange} accept="image/*" label="Upload Gambar" />
      ) : field.type === "number" ? (
        <input type="number" value={value ?? 0} onChange={(e) => onChange(Number(e.target.value))} className={base} />
      ) : (
        <input type={field.type || "text"} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} className={base} />
      )}
      {field.help && <p className="mt-1 text-xs text-slate-500">{field.help}</p>}
    </div>
  );
}

function ListArrayEditor({ title, section, field, items, onSave, placeholder, saving }) {
  const [list, setList] = useState(items);
  useEffect(() => { setList(items); }, [items]);
  const save = () => onSave({ [section]: { [field]: list.filter(Boolean) } });
  return (
    <div className="rounded-2xl border border-slate-200 p-5 bg-slate-50">
      <div className="text-sm font-semibold text-slate-800 mb-3">{title}</div>
      <div className="space-y-2">
        {list.map((v, i) => (
          <div key={i} className="flex gap-2">
            <input value={v || ""} onChange={(e) => setList(list.map((x, idx) => idx === i ? e.target.value : x))} placeholder={placeholder} className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            <button type="button" onClick={() => setList(list.filter((_, idx) => idx !== i))} className="h-9 w-9 rounded-lg border border-slate-200 hover:border-red-500 hover:text-red-500 flex items-center justify-center"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={() => setList([...list, ""])} className="btn-ghost text-xs"><Plus className="h-3 w-3" /> Tambah</button>
          <button type="button" onClick={save} disabled={saving} className="btn-secondary !py-1.5 !px-3 !text-xs"><Save className="h-3 w-3" /> Simpan</button>
        </div>
      </div>
    </div>
  );
}

function SocialsEditor({ socials, onSave, saving }) {
  const [form, setForm] = useState(socials);
  useEffect(() => { setForm(socials); }, [socials]);
  const KEYS = ["instagram", "facebook", "linkedin", "youtube", "twitter", "tiktok", "whatsapp"];
  return (
    <div className="rounded-2xl border border-slate-200 p-5 bg-slate-50">
      <div className="text-sm font-semibold text-slate-800 mb-3">Sosial Media</div>
      <div className="grid sm:grid-cols-2 gap-3">
        {KEYS.map((k) => (
          <div key={k}>
            <label className="block text-xs font-semibold text-slate-600 mb-1 capitalize">{k}</label>
            <input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={`https://${k}.com/...`} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
          </div>
        ))}
      </div>
      <button type="button" onClick={() => onSave({ contact: { socials: form } })} disabled={saving} className="btn-secondary !py-1.5 !px-3 !text-xs mt-3">
        <Save className="h-3 w-3" /> Simpan Sosial
      </button>
    </div>
  );
}

function BankAccountsEditor({ accounts, onSave, saving }) {
  const [list, setList] = useState(accounts);
  useEffect(() => { setList(accounts); }, [accounts]);
  const save = () => onSave({ bank_accounts: list });
  return (
    <div className="space-y-3 max-w-3xl">
      {list.map((b, i) => (
        <div key={i} className="card-sf p-5">
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Bank</label>
              <input value={b.bank || ""} onChange={(e) => setList(list.map((x, idx) => idx === i ? { ...x, bank: e.target.value } : x))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-slate-700 mb-1">Nama Akun</label>
              <input value={b.account_name || ""} onChange={(e) => setList(list.map((x, idx) => idx === i ? { ...x, account_name: e.target.value } : x))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            </div>
            <div className="sm:col-span-3">
              <label className="block text-xs font-semibold text-slate-700 mb-1">Nomor Rekening</label>
              <input value={b.account_number || ""} onChange={(e) => setList(list.map((x, idx) => idx === i ? { ...x, account_number: e.target.value } : x))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            </div>
          </div>
          <button type="button" onClick={() => setList(list.filter((_, idx) => idx !== i))} className="mt-3 text-xs text-red-500 hover:underline"><Trash2 className="h-3 w-3 inline" /> Hapus</button>
        </div>
      ))}
      <div className="flex gap-2">
        <button type="button" onClick={() => setList([...list, { bank: "", account_name: "Yayasan Sauyunan Rahardja Nusantara", account_number: "" }])} className="btn-outline"><Plus className="h-4 w-4" /> Tambah Rekening</button>
        <button type="button" onClick={save} disabled={saving} className="btn-secondary"><Save className="h-4 w-4" /> {saving ? "Menyimpan..." : "Simpan Semua"}</button>
      </div>
    </div>
  );
}
